export const API_BASE = process.env.REACT_APP_API_URL || '/api';
const IDENTITY_URL = 'https://identity.intechweben.com/api/v1';

export function getCookie(name) {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
  return match ? match.split('=')[1] : null;
}

export function setCookie(name, value, expires) {
  let cookie = `${name}=${value}; path=/`;
  if (expires) cookie += `; expires=${expires.toUTCString()}`;
  document.cookie = cookie;
}

export function clearSession() {
  ['jwtToken', 'renewalToken', 'displayName', 'tenantId', 'tenantName'].forEach(name => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
}

export function parseJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

function getCookieExpiry() {
  return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
}

export function isTokenValid() {
  const token = getCookie('jwtToken');
  if (!token) { console.log('[auth] isTokenValid: no jwtToken cookie'); return false; }
  const payload = parseJwtPayload(token);
  if (!payload || !payload.exp) { console.log('[auth] isTokenValid: no exp in payload'); return false; }
  const remaining = payload.exp * 1000 - Date.now();
  const valid = remaining > 5000;
  if (!valid) console.log(`[auth] isTokenValid: expired (${Math.round(remaining / 1000)}s remaining)`);
  return valid;
}

function shouldRenewSoon() {
  const token = getCookie('jwtToken');
  if (!token) return false;
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  const remaining = payload.exp * 1000 - Date.now();
  return remaining > 5000 && remaining < 120000;
}

let renewalPromise = null;

async function renewToken() {
  const renewal = getCookie('renewalToken');
  if (!renewal) { console.log('[auth] renewToken: no renewalToken cookie'); return false; }
  const renewPayload = parseJwtPayload(renewal);
  if (renewPayload?.exp) {
    const renewRemaining = renewPayload.exp * 1000 - Date.now();
    console.log(`[auth] renewToken: renewal token has ${Math.round(renewRemaining / 1000)}s remaining`);
    if (renewRemaining <= 0) { console.log('[auth] renewToken: renewal token expired'); return false; }
  }
  try {
    console.log('[auth] renewToken: calling /token/renew');
    const res = await fetch(`${IDENTITY_URL}/token/renew`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ renewalToken: renewal }),
    });
    if (!res.ok) { console.log(`[auth] renewToken: failed with status ${res.status}`); return false; }
    const data = await res.json();
    const newPayload = parseJwtPayload(data.jwtToken);
    console.log('[auth] renewToken: success, new JWT exp in', Math.round((newPayload?.exp * 1000 - Date.now()) / 1000), 's, has roles:', !!newPayload?.roles, 'has principals:', !!newPayload?.principals);
    const expires = getCookieExpiry();
    setCookie('jwtToken', data.jwtToken, expires);
    setCookie('renewalToken', data.renewalToken, expires);
    return true;
  } catch (e) {
    console.log('[auth] renewToken: exception', e.message);
    return false;
  }
}

export async function ensureValidToken() {
  if (isTokenValid()) return true;
  console.log('[auth] ensureValidToken: token invalid, attempting renewal');
  if (!renewalPromise) renewalPromise = renewToken().finally(() => { renewalPromise = null; });
  const renewed = await renewalPromise;
  if (!renewed) { console.log('[auth] ensureValidToken: renewal failed, clearing session'); clearSession(); return false; }
  return true;
}

let offline = false;
const listeners = new Set();

export function onConnectionChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function setOffline(value) {
  if (offline !== value) {
    offline = value;
    listeners.forEach(fn => fn(value));
  }
}

const OFFLINE_RESPONSE = { ok: false, status: 0, json: () => Promise.resolve([]), text: () => Promise.resolve('') };

export async function api(path, options = {}) {
  const valid = await ensureValidToken();
  if (!valid) { console.log(`[auth] api: ensureValidToken failed for ${path}, redirecting`); window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`; return OFFLINE_RESPONSE; }
  const jwtToken = getCookie('jwtToken');
  const tenantId = getCookie('tenantId');
  const headers = { ...options.headers };
  if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  if (shouldRenewSoon() && !renewalPromise) {
    renewalPromise = renewToken().finally(() => { renewalPromise = null; });
  }
  return fetch(`${API_BASE}${path}`, { ...options, headers }).then(async res => {
    setOffline(res.status >= 502 && res.status <= 504);
    if (res.status === 401) {
      console.log(`[auth] api: got 401 from ${path}, attempting renewal`);
      renewalPromise = null;
      const renewed = await renewToken();
      if (renewed) {
        const retryHeaders = { ...headers, 'Authorization': `Bearer ${getCookie('jwtToken')}` };
        return fetch(`${API_BASE}${path}`, { ...options, headers: retryHeaders });
      }
      clearSession();
      window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
    return res;
  }).catch(() => {
    setOffline(true);
    return OFFLINE_RESPONSE;
  });
}
